import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

type ContentType = 
  | "drop_comments" 
  | "event_messages" 
  | "drop_invites" 
  | "viral_stories" 
  | "hall_of_fame"
  | "fun_zone";

interface ReadStatus {
  lastReadAt: Date | null;
  unreadCount: number;
}

export const useReadStatus = () => {
  const [loading, setLoading] = useState(false);

  const getLastReadTime = useCallback(async (
    contentType: ContentType,
    contentId?: string
  ): Promise<Date | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      let query = supabase
        .from("user_read_status")
        .select("last_read_at")
        .eq("user_id", user.id)
        .eq("content_type", contentType);

      if (contentId) {
        query = query.eq("content_id", contentId);
      } else {
        query = query.is("content_id", null);
      }

      const { data } = await query.maybeSingle();
      return data?.last_read_at ? new Date(data.last_read_at) : null;
    } catch (error) {
      console.error("Error getting last read time:", error);
      return null;
    }
  }, []);

  const markAsRead = useCallback(async (
    contentType: ContentType,
    contentId?: string
  ): Promise<boolean> => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { error } = await supabase
        .from("user_read_status")
        .upsert({
          user_id: user.id,
          content_type: contentType,
          content_id: contentId || null,
          last_read_at: new Date().toISOString(),
        }, {
          onConflict: "user_id,content_type,content_id"
        });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error("Error marking as read:", error);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const markAllAsRead = useCallback(async (
    contentTypes: ContentType[]
  ): Promise<boolean> => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const now = new Date().toISOString();
      const upserts = contentTypes.map(contentType => ({
        user_id: user.id,
        content_type: contentType,
        content_id: null,
        last_read_at: now,
      }));

      const { error } = await supabase
        .from("user_read_status")
        .upsert(upserts, {
          onConflict: "user_id,content_type,content_id"
        });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error("Error marking all as read:", error);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const getUnreadCommentsCount = useCallback(async (
    dropId: string
  ): Promise<number> => {
    try {
      const lastRead = await getLastReadTime("drop_comments", dropId);
      
      let query = supabase
        .from("drop_comments")
        .select("id", { count: "exact", head: true })
        .eq("drop_id", dropId);

      if (lastRead) {
        query = query.gt("created_at", lastRead.toISOString());
      }

      const { count } = await query;
      return count || 0;
    } catch (error) {
      console.error("Error getting unread comments count:", error);
      return 0;
    }
  }, [getLastReadTime]);

  const getUnreadMessagesCount = useCallback(async (
    eventId: string
  ): Promise<number> => {
    try {
      const lastRead = await getLastReadTime("event_messages", eventId);
      
      let query = supabase
        .from("event_messages")
        .select("id", { count: "exact", head: true })
        .eq("event_id", eventId);

      if (lastRead) {
        query = query.gt("created_at", lastRead.toISOString());
      }

      const { count } = await query;
      return count || 0;
    } catch (error) {
      console.error("Error getting unread messages count:", error);
      return 0;
    }
  }, [getLastReadTime]);

  return {
    loading,
    getLastReadTime,
    markAsRead,
    markAllAsRead,
    getUnreadCommentsCount,
    getUnreadMessagesCount,
  };
};

export default useReadStatus;
